import * as React from 'react'

import { cn } from '@/lib/utils'

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  infoClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { className, infoClassName, type, max, onChange, onFocus, ...props },
    ref,
  ) => {
    const [characterCount, setCharacterCount] = React.useState(0)
    const hasValueProp = Object.prototype.hasOwnProperty.call(props, 'value')
    const inputValue = hasValueProp ? (props.value ?? '') : undefined

    React.useEffect(() => {
      const value = props.value ?? props.defaultValue

      if (type === 'text' && typeof value === 'string') {
        setCharacterCount(value.trim().length)
      }
    }, [type, props.value, props.defaultValue])

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (type === 'number') {
        event.target.value = event.target.value.replace(/^0+(?=\d)/, '')
      } else if (type === 'text') {
        setCharacterCount(event.target.value.trim().length)
      }

      onChange?.(event)
    }

    const maxNumber = typeof max === 'number' ? max : Number(max)
    const shouldRenderCount = type === 'text' && Number.isFinite(maxNumber)

    return (
      <>
        <input
          type={type}
          max={max}
          className={cn(
            'focus-visible:outline-hidden flex h-[46px] w-full rounded-sm border border-slate-200 bg-white px-6 py-4 text-p-1 text-white ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:bg-neutral-blue-800 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300',
            className,
          )}
          ref={ref}
          {...props}
          value={inputValue}
          onFocus={(event) => {
            event.target.select()
            onFocus?.(event)
          }}
          onChange={handleChange}
        />
        {shouldRenderCount && (
          <label
            className={cn(
              'mt-1 flex w-full justify-end text-p-1 text-neutral-blue-200',
              characterCount > maxNumber && 'text-error-500',
              infoClassName,
            )}
          >
            {characterCount} / {maxNumber}
          </label>
        )}
      </>
    )
  },
)

Input.displayName = 'Input'

export { Input }
